import React, { useCallback, useEffect, useMemo, useState, useRef, lazy, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { MenuParentTabs } from '../components/restaurant/MenuParentTabs';
import { CartItem as CartItemComponent } from '../components/restaurant/CartItem';
import { useRestaurantBySlug, useMenuData } from '../lib/hooks';
import { useCart } from '../lib/cart';
import { supabase } from '../lib/supabase';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, ShoppingBag, X, MessageCircle, ArrowLeft, CheckCircle2, MapPin, Bike, Flame, Plus } from 'lucide-react';
import { MenuItem, CartItemOption } from '../lib/types';
import { formatCurrency, cn } from '../lib/utils';
import { buildWhatsAppMessage, buildWhatsAppUrl } from '../lib/whatsapp';
import { AnimatePresence, motion } from 'framer-motion';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { getSubdomainSlug } from '../lib/subdomain';
import { isRestaurantOpenBySchedule } from '../lib/schedule';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useRestaurantScheduleStatus } from '../lib/schedule';
import { useOpenOrderCheck } from '../lib/open-order-check';
import { OpenOrderAlert } from '../components/restaurant/OpenOrderAlert';
import { isPreorderRestaurant, DELIVERY_TIME_SLOTS, getUpcomingSaturdays, getUpcomingDeliveryDates, CAPRICHO_DELIVERY_HOURS, formatDeliveryDate, toDateString, getOrderingWindowMessage, checkPreorderCapacity, CAPACITY_FULL_MESSAGE, fetchDeliveryTimeSlots, checkTimeSlotsAvailability, type TimeSlotWithAvailability } from '../lib/preorder';
// build-fix: 2026-05-02-isActuallyOpen
// Keep emoji helper for upsell section labels
function getCategoryEmoji(name: string): string {
  const emojiMap: Record<string, string> = {
    bebida: '🥤',
    postre: '🍮',
    taco: '🌮',
    burrito: '🌯',
    combo: '🔥',
    entrada: '🍽️',
    ensalada: '🥗',
    nacho: '🧀',
    sopa: '🍲',
    pizza: '🍕',
    hamburguesa: '🍔',
    extra: '✨',
    especial: '⭐'
  };
  const lower = name.toLowerCase().trim();
  for (const [key, emoji] of Object.entries(emojiMap)) {
    if (lower.includes(key)) return emoji;
  }
  return '🔥';
}
const SECTION_TITLES: Record<string, string> = {
  tacos: '🌮 Tacos que no fallan',
  burritos: '🌯 Burritos bien cargados',
  quesadillas: '🧀 Quesadillas irresistibles',
  bowls: '🥗 Bowls con power',
  antojitos: '🔥 Antojitos para compartir',
  hamburguesas: '🍔 Hamburguesas de verdad',
  pizzas: '🍕 Pizzas artesanales',
  combos: '💣 Combos para romper dieta',
  bebidas: '🥤 Para bajar el fuego',
  postres: '🍮 El toque dulce',
  entradas: '🍽️ Para abrir boca',
  ensaladas: '🥗 Algo más fresquito',
  extras: '✨ Extras que suman',
  especiales: '⭐ Especiales de la casa',
  nachos: '🧀 Nachos cargados',
  sopas: '🍲 Calditos reconfortantes'
};
function getCreativeTitle(name: string): string {
  const lower = name.toLowerCase().trim();
  for (const [key, title] of Object.entries(SECTION_TITLES)) {
    if (lower.includes(key)) return title;
  }
  return `🔥 ${name}`;
}
// Check if a category is an upsell category (bebidas/postres)
function isUpsellCategory(name: string): boolean {
  const lower = name.toLowerCase().trim();
  return ['bebida', 'bebidas', 'drinks', 'postre', 'postres', 'desserts', 'refresco', 'refrescos'].some(k => lower.includes(k));
}
interface PublicMenuPageProps {
  /** Optional slug from subdomain — overrides useParams */
  subdomainSlug?: string;
}
export function PublicMenuPage({
  subdomainSlug
}: PublicMenuPageProps = {}) {
  const {
    slug: pathSlug
  } = useParams<{
    slug: string;
  }>();
  // Priority: explicit subdomain prop > auto-detected subdomain > path param
  const slug = subdomainSlug || getSubdomainSlug() || pathSlug;
  const navigate = useNavigate();
  const {
    restaurant,
    settings: restaurantSettings,
    loading: restaurantLoading
  } = useRestaurantBySlug(slug);
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  // Schedule-based open/closed enforcement
  const scheduleStatus = useRestaurantScheduleStatus(restaurant?.slug);
  const isClosedBySchedule = scheduleStatus.isClosedForOrdering;
  const {
    categories: rawCategories,
    items: rawItems,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  // Auto-generate categories from subcategory field when menu_categories is empty
  // This handles restaurants like Capricho de Canela that use subcategory instead
  const {
    categories,
    items
  } = useMemo(() => {
    if (rawCategories.length > 0) {
      return {
        categories: rawCategories,
        items: rawItems
      };
    }
    if (rawItems.length === 0) {
      return {
        categories: rawCategories,
        items: rawItems
      };
    }
    // Build synthetic categories from unique subcategory values
    const subcategoryMap = new Map<string, {
      minSort: number;
      items: typeof rawItems;
    }>();
    for (const item of rawItems) {
      const sub = item.subcategory || 'Menú';
      if (!subcategoryMap.has(sub)) {
        subcategoryMap.set(sub, {
          minSort: item.sort_order,
          items: []
        });
      }
      subcategoryMap.get(sub)!.items.push(item);
      subcategoryMap.get(sub)!.minSort = Math.min(subcategoryMap.get(sub)!.minSort, item.sort_order);
    }
    // Sort subcategories by their earliest item sort_order
    const sortedSubs = [...subcategoryMap.entries()].sort((a, b) => a[1].minSort - b[1].minSort);
    const syntheticCategories = sortedSubs.map(([sub, data], idx) => ({
      id: `sub-${sub}`,
      restaurant_id: restaurant?.id || '',
      name: sub,
      slug: sub.toLowerCase().replace(/\s+/g, '-'),
      description: null,
      image_url: null,
      sort_order: idx,
      is_active: true,
      parent_category_id: null,
      is_parent: false,
      created_at: '',
      updated_at: ''
    }));
    // Remap items so category_id matches the synthetic category id
    const remappedItems = rawItems.map(item => ({
      ...item,
      category_id: `sub-${item.subcategory || 'Menú'}`
    }));
    return {
      categories: syntheticCategories,
      items: remappedItems
    };
  }, [rawCategories, rawItems, restaurant?.id]);
  const {
    cart,
    addItem,
    updateQuantity,
    removeItem,
    getTotal,
    getItemCount,
    clearCart,
    getDiscount
  } = useCart();
  const {
    openOrder,
    shouldAlert,
    dismiss: dismissOpenOrder
  } = useOpenOrderCheck(restaurant?.id);
  const [showOpenOrderAlert, setShowOpenOrderAlert] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{
    item: MenuItem;
    quantity: number;
    options: CartItemOption[];
    notes: string;
  } | null>(null);
  // Register restaurant with central kitchen pause monitor
  useEffect(() => {
    if (restaurant?.id) {
      console.log('[NOIZA][PublicMenuPage] Registering restaurant:', restaurant.id, 'slug:', slug);
      monitorRestaurant(restaurant.id);
    }
  }, [restaurant?.id, monitorRestaurant]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [animKey, setAnimKey] = useState(0); // for triggering re-animation
  // Checkout state
  const [cartStep, setCartStep] = useState<'review' | 'checkout' | 'success'>('review');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryType, setDeliveryType] = useState<'pickup' | 'delivery'>('delivery');
  const [orderNotes, setOrderNotes] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryLat, setDeliveryLat] = useState<number | null>(null);
  const [deliveryLng, setDeliveryLng] = useState<number | null>(null);
  const [mapsUrl, setMapsUrl] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<{
    orderNumber: string;
    trackingToken: string;
    whatsappUrl: string;
    orderId: string;
    whatsappSent?: boolean;
  } | null>(null);
  // Preorder state (Capricho de Canela)
  const [preorderDate, setPreorderDate] = useState('');
  const [preorderTimeSlot, setPreorderTimeSlot] = useState('');
  const [preorderCapacityFull, setPreorderCapacityFull] = useState(false);
  const [checkingCapacity, setCheckingCapacity] = useState(false);
  const [dynamicTimeSlots, setDynamicTimeSlots] = useState<TimeSlotWithAvailability[]>([]);
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false);
  const [deliveryHour, setDeliveryHour] = useState('');
  const [showCaprichoWelcome, setShowCaprichoWelcome] = useState(false);
  const isCapricho = String(restaurant?.id) === '2' || restaurant?.slug === 'capricho-de-canela';
  const isActuallyOpen = scheduleStatus.hasSchedule ? scheduleStatus.isOpen : true;
  // Check preorder capacity + load time slots when date is selected
  const handlePreorderDateSelect = async (dateStr: string) => {
    setPreorderDate(dateStr);
    setPreorderTimeSlot('');
    setPreorderCapacityFull(false);
    setDynamicTimeSlots([]);
    if (restaurant?.id && isPreorderRestaurant(restaurant.id)) {
      setCheckingCapacity(true);
      setTimeSlotsLoading(true);
      // Check overall Saturday capacity
      const capacityResult = await checkPreorderCapacity(restaurant.id, dateStr);
      setPreorderCapacityFull(!capacityResult.available);
      // Load time slots with per-slot availability
      if (capacityResult.available) {
        const slots = await fetchDeliveryTimeSlots(restaurant.id);
        const slotsWithAvailability = await checkTimeSlotsAvailability(restaurant.id, dateStr, slots);
        setDynamicTimeSlots(slotsWithAvailability);
      }
      setCheckingCapacity(false);
      setTimeSlotsLoading(false);
    }
  };
  // Show Capricho welcome modal on first visit
  useEffect(() => {
    if (isCapricho && restaurant) {
      const dismissed = sessionStorage.getItem('capricho-welcome-dismissed');
      if (!dismissed) {
        setShowCaprichoWelcome(true);
      }
    }
  }, [isCapricho, restaurant]);
  // Set default delivery type based on restaurant_settings
  useEffect(() => {
    if (restaurantSettings) {
      if (restaurantSettings.accept_delivery) {
        setDeliveryType('delivery');
      } else if (restaurantSettings.accept_pickup) {
        setDeliveryType('pickup');
      }
    }
  }, [restaurantSettings]);
  const [activeParent, setActiveParent] = useState<string>('');
  // Lines 330-346: Update parentCategories, hasParents, leafCategories, currentSubcategories
  const parentCategories = useMemo(() => {
    return categories.filter(c => c.is_parent === true && !c.parent_category_id && c.is_active !== false).sort((a, b) => a.sort_order - b.sort_order);
  }, [categories]);
  const hasParents = parentCategories.length > 0;
  // Non-parent, non-child leaf categories (for flat nav fallback)
  const leafCategories = useMemo(() => {
    if (!hasParents) return categories.filter(c => !c.is_parent);
    return categories.filter(c => !c.is_parent && !!c.parent_category_id);
  }, [categories, hasParents]);
  const currentSubcategories = useMemo(() => {
    if (!activeParent) return [];
    return categories.filter(c => String(c.parent_category_id) === String(activeParent) && !c.is_parent && c.is_active !== false).sort((a, b) => a.sort_order - b.sort_order);
  }, [categories, activeParent]);
  // Set initial active category and parent
  useEffect(() => {
    if (categories.length > 0) {
      if (hasParents && !activeParent) {
        const firstParent = parentCategories[0];
        setActiveParent(String(firstParent.id));
        const firstSub = categories.find(c => String(c.parent_category_id) === String(firstParent.id));
        if (firstSub) setActiveCategory(String(firstSub.id));
      } else if (!hasParents && !activeCategory) {
        setActiveCategory(String(categories[0].id));
      }
    }
  }, [categories, hasParents, activeParent, activeCategory, parentCategories]);
  const handleParentSelect = (parentId: string) => {
    setActiveParent(String(parentId));
    const firstSub = categories.find(c => String(c.parent_category_id) === String(parentId) && !c.is_parent);
    if (firstSub) {
      setActiveCategory(String(firstSub.id));
    } else {
      setActiveCategory('');
    }
  };
  // Reset cart step when cart opens/closes
  useEffect(() => {
    if (!isCartOpen) {
      setCartStep('review');
      setSubmitError(null);
    }
  }, [isCartOpen]);
  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory(categoryId);
    // No scroll — subcategories act as filters, content swaps in place
  };
  // No scroll observer — subcategories act as pure filters
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    if (isKitchenPaused) return;
    if (isClosedBySchedule) {
      alert(`${restaurant.name} está cerrado en este momento. ${scheduleStatus.nextOpeningHint || 'Vuelve durante nuestro horario de atención.'}`);
      return;
    }
    // Show open order alert on first add attempt
    if (shouldAlert) {
      console.log('[OpenOrderCheck] Intercepting add-to-cart — showing alert');
      setPendingAdd({
        item,
        quantity,
        options,
        notes
      });
      setShowOpenOrderAlert(true);
      return;
    }
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options,
      station_code: item.station_code || null
    }, restaurant.name, restaurant.slug);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    } else {
      setIsCartOpen(true);
    }
  };
  // Quick add for upsell (no options, quantity 1)
  const handleQuickAdd = (item: MenuItem) => {
    if (!restaurant) return;
    if (isClosedBySchedule) {
      alert(`${restaurant.name} está cerrado en este momento. ${scheduleStatus.nextOpeningHint || 'Vuelve durante nuestro horario de atención.'}`);
      return;
    }
    // Show open order alert on first add attempt
    if (shouldAlert) {
      console.log('[OpenOrderCheck] Intercepting quick-add — showing alert');
      setPendingAdd({
        item,
        quantity: 1,
        options: [],
        notes: ''
      });
      setShowOpenOrderAlert(true);
      return;
    }
    addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity: 1,
      notes: '',
      image_url: item.image_url,
      selected_options: [],
      station_code: item.station_code || null
    }, restaurant.name, restaurant.slug);
  };
  // Intercept checkout: show upsell first, then navigate to cart page
  const handleProceedToCheckout = () => {
    // Check if there are upsell items available
    const upsellItems = getUpsellItems();
    if (upsellItems.length > 0) {
      setShowUpsellModal(true);
    } else {
      setIsCartOpen(false);
      const s = restaurant?.slug || cart.restaurantSlug || slug;
      navigate(s ? `/r/${s}/cart` : '/cart');
    }
  };
  // Get upsell items (bebidas + postres) — only from leaf categories
  const getUpsellItems = () => {
    const upsellCats = leafCategories.filter(c => isUpsellCategory(c.name));
    return items.filter(item => upsellCats.some(c => c.id === item.category_id));
  };
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Tu navegador no soporta geolocalización. Ingresa tu dirección manualmente.');
      return;
    }
    setIsLocating(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(position => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      setDeliveryLat(lat);
      setDeliveryLng(lng);
      setMapsUrl(`https://www.google.com/maps?q=${lat},${lng}`);
      setIsLocating(false);
      setLocationError(null);
      console.log('[NOIZA][public] Location captured:', {
        lat,
        lng
      });
    }, error => {
      setIsLocating(false);
      console.error('[NOIZA][public] Geolocation error:', error);
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setLocationError('Permiso de ubicación denegado. Ingresa tu dirección manualmente.');
          break;
        case error.POSITION_UNAVAILABLE:
          setLocationError('No se pudo obtener tu ubicación. Ingresa tu dirección manualmente.');
          break;
        default:
          setLocationError('Error al obtener ubicación. Ingresa tu dirección manualmente.');
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  // Order submission is handled by /checkout via the create-order edge function.
  // This function redirects to the cart page instead of doing direct Supabase inserts.
  const handleSubmitOrder = () => {
    setIsCartOpen(false);
    const s = restaurant?.slug || cart.restaurantSlug || slug;
    navigate(s ? `/r/${s}/cart` : '/cart');
  };
  // ─── LOADING ───
  if (restaurantLoading || menuLoading) {
    return <div className="min-h-screen w-full flex flex-col bg-[#0B0B0B]" data-id="element-5804">
        <div className="h-[60vh] sm:h-[50vh] w-full bg-[#141414] animate-pulse" data-id="element-5805" />
        <div className="max-w-4xl mx-auto px-4 w-full mt-8" data-id="element-5806">
          <Skeleton className="h-8 w-48 mb-6 bg-[#141414]" data-id="element-5807" />
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-8" data-id="element-5808">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="aspect-square rounded-2xl bg-[#141414]" data-id="element-5809" />)}
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-5810">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="aspect-[4/5] rounded-2xl bg-[#141414]" data-id="element-5811" />)}
          </div>
        </div>
      </div>;
  }
  // ─── NOT FOUND ───
  if (!restaurant) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B] p-4 text-[#F8F5F0]" data-id="element-5812">
        <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe o no está disponible." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-5813" />
      </div>;
  }
  const cartCount = getItemCount();
  const cartTotal = getTotal();
  // Active category items
  const activeCategoryObj = categories.find(c => c.id === activeCategory);
  const activeCategoryItems = items.filter(item => item.category_id === activeCategory);
  // Upsell items
  const upsellItems = getUpsellItems();
  // Group upsell items by category for display
  const upsellGroups = leafCategories.filter(c => isUpsellCategory(c.name)).map(c => ({
    category: c,
    items: items.filter(i => i.category_id === c.id)
  })).filter(g => g.items.length > 0);
  // Get featured items
  const featuredItems = items.filter((item: any) => item.featured === true || item.is_featured === true);
  // If not enough featured items, grab a few that have images
  const CAPRICHO_FEATURED_NAMES = ['clásico', 'clasico', 'oreo', 'crunch'];
  const displayFeatured = isCapricho ? items.filter(i => {
    const lower = i.name.toLowerCase();
    return CAPRICHO_FEATURED_NAMES.some(n => lower.includes(n));
  }) : featuredItems.length >= 3 ? featuredItems : [...featuredItems, ...items.filter(i => i.image_url && !featuredItems.find(f => f.id === i.id))].slice(0, 6);
  return <div className={cn('min-h-screen w-full flex flex-col pb-28 relative', isCapricho ? 'bg-[#FBF7F2]' : 'bg-[#0B0B0B]')} data-id="element-5814">
      {/* ═══ NOIZA HEADER ═══ */}
      <MarketplaceHeader data-id="element-5815" />

      {/* ═══ BACKGROUND TEXTURE ═══ */}
      {isCapricho ? <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true" data-id="element-5816">
          <div className="w-full h-full opacity-[0.04]" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/qfQUG6xSwCibNddEpouoD3/ChatGPT_Image_21_mar_2026,_21_58_30.png)',
        backgroundSize: '320px',
        backgroundRepeat: 'repeat',
        backgroundPosition: 'center'
      }} data-id="element-5817" />
        </div> : <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-5818">
          <img src="https://cdn.magicpatterns.com/uploads/6fAo78uf9tdtomvjsiMQAe/MX.jpg" alt="" className="w-full h-full object-cover opacity-[0.06]" loading="eager" decoding="async" aria-hidden="true" data-id="element-5819" />
        </div>}

      {/* ═══ HERO — RestaurantPage style ═══ */}
      <div className={cn('relative w-full overflow-hidden', isCapricho ? 'bg-[#FBF7F2]' : 'bg-[#0B0B0B]')} data-id="element-5820">
        {/* Cover Image */}
        <div className={cn('relative h-44 sm:h-56 md:h-64 w-full', isCapricho ? 'bg-[#F5E9DA]' : 'bg-[#141414]')} data-id="element-5821">
          {restaurant.cover_url || isCapricho ? <img src={isCapricho ? 'https://cdn.magicpatterns.com/uploads/wXcpDT288AkzVxuPArRvs7/ChatGPT_Image_21_mar_2026,_22_01_56.png' : restaurant.cover_url!} alt="Cover" className="absolute inset-0 w-full h-full object-cover" loading="eager" decoding="async" data-id="element-5822" /> : <div className="absolute inset-0 w-full h-full" style={{
          background: 'linear-gradient(160deg, #006847 0%, #004d35 40%, #0B0B0B 100%)'
        }} data-id="element-5823" />}
          <div className={cn('absolute inset-0 bg-gradient-to-t', isCapricho ? 'from-[#FBF7F2] via-[#FBF7F2]/40 to-transparent' : 'from-[#0B0B0B] via-black/40 to-transparent')} data-id="element-5824" />

          {/* Open/Closed Badge */}
          <div className="absolute top-4 right-4 z-10" data-id="element-5825">
            <div className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-full border', isCapricho ? 'bg-white/90 border-[#E8DDD0] shadow-sm' : 'bg-black/70 border-white/10')} data-id="element-5826">
              <div className={cn('w-2 h-2 rounded-full', isActuallyOpen ? 'bg-[#2ECC71]' : 'bg-[#CE1126]')} data-id="element-5827" />
              <span className={cn('text-xs font-medium', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5828">
                {isActuallyOpen ? 'Abierto ahora' : 'Cerrado'}
              </span>
            </div>
          </div>
        </div>

        {/* Content overlay — RestaurantPage style */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative" data-id="element-5829">
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-end -mt-10 sm:-mt-14 mb-4" data-id="element-5830">
            {/* Logo */}
            <div className={cn('relative rounded-2xl p-1 shadow-xl border shrink-0', isCapricho ? 'w-24 h-24 sm:w-32 sm:h-32 bg-[#FBF7F2] border-[#E8DDD0]' : 'w-20 h-20 sm:w-28 sm:h-28 bg-[#0B0B0B] border-white/10')} data-id="element-5831">
              {isCapricho ? <img src="https://cdn.magicpatterns.com/uploads/qfQUG6xSwCibNddEpouoD3/ChatGPT_Image_21_mar_2026,_21_58_30.png" alt={`${restaurant.name} logo`} className="w-full h-full rounded-xl object-contain" loading="eager" decoding="async" data-id="element-5832" /> : restaurant.logo_url ? <img src={restaurant.logo_url} alt={`${restaurant.name} logo`} className="w-full h-full rounded-xl object-cover" loading="eager" decoding="async" data-id="element-5833" /> : <img src="https://cdn.magicpatterns.com/uploads/66K7i7HxpjApVZJyeR9rjp/ChatGPT_Image_15_mar_2026,_13_53_22.png" alt="NOIZA" className="w-full h-full rounded-xl object-cover" decoding="async" data-id="element-5834" />}
            </div>

            {/* Title & Info */}
            <div className="flex-1 pb-2" data-id="element-5835">
              <h1 className={cn('text-2xl sm:text-4xl font-black mb-1 tracking-tight', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
              fontFamily: "'Playfair Display', serif"
            } : undefined} data-id="element-5836">
                {restaurant.name}
              </h1>
              {restaurant.description_short && <p className={cn('text-sm sm:text-base max-w-2xl mb-3', isCapricho ? 'text-[#8B7355]' : 'text-white/50')} data-id="element-5837">
                  {restaurant.description_short}
                </p>}
            </div>
          </div>

          {/* Delivery badges + CTA */}
          <div className="flex flex-wrap items-center gap-2.5 pb-5" data-id="element-5838">
            {isCapricho ? <>
                {restaurantSettings?.accept_delivery !== false && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-[#8B5E3C] bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-5839">
                    <Bike className="w-3.5 h-3.5" data-id="element-5840" /> Delivery
                  </span>}
                {restaurantSettings?.accept_pickup !== false && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-[#8B5E3C] bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-5841">
                    <ShoppingBag className="w-3.5 h-3.5" data-id="element-5842" /> Para llevar
                  </span>}
                <p className="w-full text-xs text-[#8B7355] mt-1" data-id="element-5843">
                  📍 Entregas los fines de semana en San Marcos, San Pablo,
                  Santa María y alrededores
                </p>
              </> : <>
                {restaurantSettings?.accept_delivery !== false && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10" style={{
              background: 'linear-gradient(135deg, #006847 0%, #2AABB3 100%)'
            }} data-id="element-5844">
                    <Bike className="w-3.5 h-3.5" data-id="element-5845" /> Delivery
                  </span>}
                {restaurantSettings?.accept_pickup !== false && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10" style={{
              background: 'linear-gradient(135deg, #CE1126 0%, #E8751A 100%)'
            }} data-id="element-5846">
                    <ShoppingBag className="w-3.5 h-3.5" data-id="element-5847" /> Para llevar
                  </span>}
              </>}
            <button onClick={() => {
            const el = document.getElementById('menu-start');
            if (el) {
              el.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }} className={cn('ml-auto px-5 py-2 rounded-full text-xs sm:text-sm font-bold shadow-lg active:scale-95 transition-transform flex items-center gap-1.5', isCapricho ? 'bg-[#8B5E3C] text-white hover:bg-[#704A2E]' : 'bg-[#006847] text-white')} data-id="element-5848">
              Hacé tu pedido <Flame className="w-4 h-4" data-id="element-5849" />
            </button>
          </div>
        </div>
      </div>

      {categories.length === 0 ? <div className="relative z-10 max-w-4xl mx-auto w-full px-4 py-12" data-id="element-5850">
          <EmptyState icon={Store} title="Menú no disponible" description="Este restaurante aún no ha configurado su menú." data-id="element-5851" />
        </div> : <div id="menu-start" className="relative z-10 max-w-6xl mx-auto w-full flex flex-col sm:flex-row items-start gap-6 px-0 sm:px-6 pt-0 sm:pt-8" data-id="element-5852">
          {/* ═══ MENU NAVIGATION ═══ */}
          {!hasParents && <>
              {/* Mobile: Sticky horizontal pills */}
              <div className={cn('sm:hidden sticky top-0 z-30 w-full border-b px-3 py-3 overflow-x-auto no-scrollbar flex items-center gap-2', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/5')} data-id="element-5853">
                {leafCategories.filter(category => {
            if (!category.is_active) return false;
            // For Capricho: hide "Rollos de Canela" from mobile nav
            if (isCapricho) {
              const lower = category.name.toLowerCase();
              return !lower.includes('rollos de canela');
            }
            return true;
          }).map(category => {
            const isActive = activeCategory === category.id;
            return <button key={category.id} onClick={() => handleCategorySelect(category.id)} className={cn('whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 border', isActive ? isCapricho ? 'bg-[#8B5E3C] text-white border-[#8B5E3C] shadow-md shadow-[#8B5E3C]/20' : 'bg-[#006847] text-white border-[#006847] shadow-lg shadow-[#006847]/20' : isCapricho ? 'bg-white text-[#8B7355] border-[#E8DDD0] hover:border-[#8B5E3C]/30' : 'bg-[#141414] text-white/70 border-white/10 hover:border-white/20')} data-id="element-5854">
                        <span className="text-sm" data-id="element-5855">
                          {getCategoryEmoji(category.name)}
                        </span>
                        {category.name}
                      </button>;
          })}
              </div>

              {/* Desktop: Sticky left sidebar */}
              <div className={cn('hidden sm:flex sticky top-24 w-64 shrink-0 flex-col gap-1 pr-6 border-r max-h-[calc(100vh-8rem)] overflow-y-auto no-scrollbar', isCapricho ? 'border-[#E8DDD0]' : 'border-white/5')} data-id="element-5856">
                <h3 className={cn('text-xs font-black uppercase tracking-widest mb-3 px-3', isCapricho ? 'text-[#8B7355]/60' : 'text-white/40')} data-id="element-5857">
                  Menú
                </h3>
                {leafCategories.filter(category => {
            if (!category.is_active) return false;
            // For Capricho: hide "Rollos de Canela" from nav (already in featured)
            if (isCapricho) {
              const lower = category.name.toLowerCase();
              return !lower.includes('rollos de canela');
            }
            return true;
          }).map(category => {
            const isActive = activeCategory === category.id;
            return <button key={category.id} onClick={() => handleCategorySelect(category.id)} className={cn('w-full text-left px-3 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-3', isActive ? isCapricho ? 'bg-[#F5E9DA] text-[#8B5E3C]' : 'bg-[#006847]/10 text-[#F4D35E]' : isCapricho ? 'text-[#8B7355] hover:bg-white hover:text-[#3D2B1F]' : 'text-white/70 hover:bg-white/5 hover:text-white')} data-id="element-5858">
                        <span className="text-lg" data-id="element-5859">
                          {getCategoryEmoji(category.name)}
                        </span>
                        {category.name}
                      </button>;
          })}
              </div>
            </>}

          {/* ═══ MAIN CONTENT AREA ═══ */}
          <div className={cn('flex-1 w-full px-3 sm:px-0 pb-12', hasParents ? 'max-w-4xl mx-auto' : '')} data-id="element-5860">
            {hasParents && <div className="mb-4 sm:mb-6 sm:-mt-4" data-id="element-5861">
                <MenuParentTabs parents={parentCategories.map(p => ({
            key: String(p.id),
            label: p.name,
            icon: getCategoryEmoji(p.name)
          }))} activeParent={activeParent} onSelectParent={handleParentSelect} subcategories={currentSubcategories} activeSubcategory={activeCategory} onSelectSubcategory={handleCategorySelect} isDark={!isCapricho} primaryColor={isCapricho ? '#8B5E3C' : '#006847'} bgClassName={isCapricho ? 'bg-[#FBF7F2]/95' : 'bg-[#0B0B0B]/95'} data-id="element-5862" />
              </div>}

            {/* PROMO BANNER — Capricho 2do al 50% */}
            {isCapricho && <div className="mb-6 sm:mb-8 rounded-2xl overflow-hidden border-2 border-[#8B5E3C]/20 bg-gradient-to-r from-[#FFF8F0] via-[#FFF1E0] to-[#FFF8F0] p-4 sm:p-5 flex items-center gap-4" data-id="element-5863">
                <div className="text-4xl sm:text-5xl shrink-0" data-id="element-5864">🎉</div>
                <div className="flex-1 min-w-0" data-id="element-5865">
                  <p className="text-lg sm:text-xl font-black text-[#3D2B1F] leading-tight" style={{
              fontFamily: "'Playfair Display', serif"
            }} data-id="element-5866">
                    ¡2do producto a mitad de precio!
                  </p>
                </div>
              </div>}
            {/* FEATURED SECTION */}
            {displayFeatured.length > 0 && <section className="mb-10 sm:mb-14 pt-4 sm:pt-0" data-id="element-5867">
                <div className="flex items-center gap-2 mb-4 sm:mb-6 px-1" data-id="element-5868">
                  <span className="text-2xl" data-id="element-5869">⭐</span>
                  <h2 className={cn('text-xl sm:text-2xl font-black tracking-tight', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
              fontFamily: "'Playfair Display', serif"
            } : undefined} data-id="element-5870">
                    Destacados de la casa
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5" data-id="element-5871">
                  {displayFeatured.map((item: any) => <div key={`featured-${item.id}`} data-id="element-5872">
                      <MenuItemCard item={item} onClick={handleProductClick} theme={isCapricho ? 'capricho' : 'default'} data-id="element-5873" />
                    </div>)}
                </div>
              </section>}

            {/* ACTIVE CATEGORY — filter-based, no scroll */}
            <AnimatePresence mode="wait" data-id="element-5874">
              {(() => {
            const activeCat = categories.find(c => c.id === activeCategory);
            const filteredItems = activeCat ? items.filter(item => item.category_id === activeCat.id) : [];
            if (!activeCat || filteredItems.length === 0) return null;
            return <motion.section key={activeCat.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.18,
              ease: 'easeOut'
            }} data-id="element-5875">
                    <div className={cn('flex items-center justify-between mb-4 sm:mb-6 px-1 border-b pb-3', isCapricho ? 'border-[#E8DDD0]' : 'border-white/5')} data-id="element-5876">
                      <h2 className={cn('text-xl sm:text-2xl font-black tracking-tight', isCapricho ? 'text-[#8B5E3C]' : 'text-[#F4D35E]')} style={isCapricho ? {
                  fontFamily: "'Playfair Display', serif"
                } : undefined} data-id="element-5877">
                        {getCreativeTitle(activeCat.name)}
                      </h2>
                      <span className={cn('text-xs font-bold px-2.5 py-1 rounded-full', isCapricho ? 'text-[#8B7355] bg-white border border-[#E8DDD0]' : 'text-white/30 bg-white/5')} data-id="element-5878">
                        {filteredItems.length}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5" data-id="element-5879">
                      {filteredItems.map(item => <div key={item.id} data-id="element-5880">
                          <MenuItemCard item={item} onClick={handleProductClick} theme={isCapricho ? 'capricho' : 'default'} data-id="element-5881" />
                        </div>)}
                    </div>
                  </motion.section>;
          })()}
            </AnimatePresence>
          </div>
        </div>}

      {/* ═══ FLOATING CART BUTTON ═══ */}
      {cartCount > 0 && !isCartOpen && <div className="fixed bottom-4 sm:bottom-6 left-0 right-0 z-40 px-3 sm:px-4 pointer-events-none" style={{
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }} data-id="element-5882">
          <div className="max-w-4xl mx-auto flex justify-center sm:justify-end" data-id="element-5883">
            <button onClick={() => setIsCartOpen(true)} className={cn('pointer-events-auto flex items-center justify-between w-full sm:w-auto sm:min-w-[320px] h-12 sm:h-14 px-5 sm:px-6 rounded-full text-white shadow-lg active:scale-95 transition-transform', isCapricho ? 'bg-[#8B5E3C] shadow-[#8B5E3C]/20 hover:bg-[#704A2E]' : 'bg-[#006847]')} data-id="element-5884">
              <div className="flex items-center gap-3" data-id="element-5885">
                <div className={cn('w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm', isCapricho ? 'bg-[#F5E9DA] text-[#8B5E3C]' : 'bg-[#F4D35E] text-[#0B0B0B]')} data-id="element-5886">
                  {cartCount}
                </div>
                <span className="font-bold text-[15px]" data-id="element-5887">Ver pedido</span>
              </div>
              <span className="font-bold text-[15px]" data-id="element-5888">
                {formatCurrency(cartTotal)}
              </span>
            </button>
          </div>
        </div>}

      {/* ═══ CART & CHECKOUT DRAWER ═══ */}
      {isCartOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-5889">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm anim-fade-in" onClick={() => setIsCartOpen(false)} data-id="element-5890" />
          <div className={cn('relative w-full sm:w-[480px] h-full shadow-2xl flex flex-col anim-slide-in-right', isCapricho ? 'bg-white' : 'bg-[#0B0B0B] border-l border-white/10')} data-id="element-5891">
            {/* Header */}
            <div className={cn('flex items-center justify-between px-6 py-5 border-b shrink-0', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/10')} data-id="element-5892">
              <div className="flex items-center gap-3" data-id="element-5893">
                {cartStep !== 'review' && cartStep !== 'success' && <button onClick={() => setCartStep('review')} className={cn('p-2 -ml-2 rounded-full transition-colors', isCapricho ? 'hover:bg-white text-[#8B7355]' : 'hover:bg-white/10 text-white')} data-id="element-5894">
                    <ArrowLeft className="w-5 h-5" data-id="element-5895" />
                  </button>}
                <h2 className={cn('text-xl font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
              fontFamily: "'Playfair Display', serif"
            } : undefined} data-id="element-5896">
                  {cartStep === 'review' ? 'Tu Pedido' : cartStep === 'checkout' ? 'Finalizar Pedido' : '¡Pedido Confirmado!'}
                </h2>
              </div>
              <button onClick={() => setIsCartOpen(false)} className={cn('p-2 -mr-2 rounded-full transition-colors', isCapricho ? 'hover:bg-white text-[#8B7355]' : 'hover:bg-white/10 text-white')} data-id="element-5897">
                <X className="w-6 h-6" data-id="element-5898" />
              </button>
            </div>

            {/* Step: Review */}
            {cartStep === 'review' && <>
                <div className="flex-1 overflow-y-auto p-6" data-id="element-5899">
                  {cart.items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-5900">
                      <div className={cn('w-20 h-20 rounded-full flex items-center justify-center mb-6', isCapricho ? 'bg-[#F5E9DA]' : 'bg-[#141414]')} data-id="element-5901">
                        <ShoppingBag className={cn('w-8 h-8', isCapricho ? 'text-[#8B5E3C]' : 'text-white/20')} data-id="element-5902" />
                      </div>
                      <p className={cn('text-lg font-bold mb-2', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5903">
                        Tu carrito está vacío
                      </p>
                      <p className={cn('text-sm', isCapricho ? 'text-[#8B7355]' : 'text-white/50')} data-id="element-5904">
                        Agrega algunos productos deliciosos para comenzar.
                      </p>
                      <button onClick={() => setIsCartOpen(false)} className={cn('mt-8 px-6 py-3 rounded-xl text-sm font-bold transition-colors', isCapricho ? 'bg-[#FBF7F2] text-[#8B5E3C] border border-[#E8DDD0] hover:bg-[#F5E9DA]' : 'bg-white/10 text-white hover:bg-white/20')} data-id="element-5905">
                        Ver menú
                      </button>
                    </div> : <div className="space-y-4" data-id="element-5906">
                      {cart.items.map(item => <CartItemComponent key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} theme={isCapricho ? 'capricho' : 'default'} data-id="element-5907" />)}
                    </div>}
                </div>

                {cart.items.length > 0 && <div className={cn('p-6 border-t shrink-0', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/10')} data-id="element-5908">
                    {(() => {
              const disc = getDiscount();
              const rawSubtotal = disc ? cartTotal + disc.amount : cartTotal;
              return <>
                          {disc && <>
                              <div className="flex justify-between items-center mb-2" data-id="element-5909">
                                <span className={cn('font-medium text-sm', isCapricho ? 'text-[#8B7355]' : 'text-white/60')} data-id="element-5910">
                                  Subtotal
                                </span>
                                <span className={cn('text-sm', isCapricho ? 'text-[#8B7355] line-through' : 'text-white/40 line-through')} data-id="element-5911">
                                  {formatCurrency(rawSubtotal)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center mb-2" data-id="element-5912">
                                <span className="font-medium text-sm text-emerald-600" data-id="element-5913">
                                  {disc.label}
                                </span>
                                <span className="text-sm font-bold text-emerald-600" data-id="element-5914">
                                  -{formatCurrency(disc.amount)}
                                </span>
                              </div>
                            </>}
                          <div className="flex justify-between items-center mb-6" data-id="element-5915">
                            <span className={cn('font-medium', isCapricho ? 'text-[#8B7355]' : 'text-white/60')} data-id="element-5916">
                              {disc ? 'Total' : 'Subtotal'}
                            </span>
                            <span className={cn('text-xl font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5917">
                              {formatCurrency(cartTotal)}
                            </span>
                          </div>
                        </>;
            })()}
                    <div className="flex gap-3" data-id="element-5918">
                      <button onClick={() => setIsCartOpen(false)} className={cn('flex-1 h-14 rounded-xl text-[15px] font-bold border-2 transition-colors', isCapricho ? 'border-[#8B5E3C] text-[#8B5E3C] bg-white hover:bg-[#F5E9DA]' : 'border-white/20 text-white bg-transparent hover:bg-white/5')} data-id="element-5919">
                        Seguir pidiendo
                      </button>
                      <button onClick={() => {
                setIsCartOpen(false);
                const s = restaurant?.slug || cart.restaurantSlug || slug;
                navigate(s ? `/r/${s}/cart` : '/cart');
              }} className={cn('flex-1 h-14 rounded-xl text-[15px] font-bold shadow-lg transition-colors', isCapricho ? 'bg-[#8B5E3C] text-white hover:bg-[#704A2E] shadow-[#8B5E3C]/20' : 'bg-[#006847] text-white active:bg-[#005a3d]')} data-id="element-5920">
                        Checkout
                      </button>
                    </div>
                    <button onClick={() => {
              if (confirm('¿Estás seguro de vaciar el carrito?')) clearCart();
            }} className={cn('w-full text-center text-sm font-medium transition-colors py-3 mt-2', isCapricho ? 'text-[#8B7355] hover:text-[#CE1126]' : 'text-white/30 hover:text-[#CE1126]')} data-id="element-5921">
                      Vaciar carrito
                    </button>
                  </div>}
              </>}

            {/* Step: Checkout — REDIRECTS to /r/:slug/cart (inline checkout removed) */}
            {cartStep === 'checkout' && (() => {
          // Safety net: if somehow cartStep is 'checkout', redirect to cart page
          const s = restaurant?.slug || cart.restaurantSlug || slug;
          const cartUrl = s ? `/r/${s}/cart` : '/cart';
          window.location.href = cartUrl;
          return null;
        })()}

            {/* REMOVED: Old inline checkout form that caused RLS errors by inserting directly into order_items */}
            {false && <>
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-id="element-5922">
                  <div className="space-y-4" data-id="element-5923">
                    <div className="space-y-1.5" data-id="element-5924">
                      <label className={cn('block text-sm font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5925">
                        Tu nombre
                      </label>
                      <input type="text" placeholder="Ej: María López" value={customerName} onChange={e => setCustomerName(e.target.value)} required className={cn('w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:border-transparent', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#3D2B1F] placeholder-[#8B7355]/50 focus:ring-[#8B5E3C]' : 'bg-[#141414] border-white/10 text-white placeholder-white/30 focus:ring-[#006847]')} data-id="element-5926" />
                    </div>
                    <div className="space-y-1.5" data-id="element-5927">
                      <label className={cn('block text-sm font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5928">
                        Tu teléfono
                      </label>
                      <input type="tel" placeholder="Ej: 8888-8888" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} required className={cn('w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:border-transparent', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#3D2B1F] placeholder-[#8B7355]/50 focus:ring-[#8B5E3C]' : 'bg-[#141414] border-white/10 text-white placeholder-white/30 focus:ring-[#006847]')} data-id="element-5929" />
                    </div>
                  </div>

                  {/* Preorder Schedule Banner */}
                  {isPreorderRestaurant(restaurant?.id) && (() => {
              const windowInfo = getOrderingWindowMessage();
              const deliveryDates = isCapricho ? getUpcomingDeliveryDates(6) : getUpcomingSaturdays(3);
              return <div className="space-y-4" data-id="element-5930">
                          {/* Schedule Info Banner */}
                          <div className={cn('rounded-xl p-4 border', windowInfo.isOpen ? isCapricho ? 'bg-[#F5E9DA] border-[#E8DDD0]' : 'bg-[#006847]/10 border-[#006847]/30' : 'bg-[#CE1126]/10 border-[#CE1126]/30')} data-id="element-5931">
                            <p className={cn('font-bold text-sm mb-1', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5932">
                              {windowInfo.title}
                            </p>
                            <p className={cn('text-xs leading-relaxed', isCapricho ? 'text-[#8B7355]' : 'text-white/60')} data-id="element-5933">
                              {windowInfo.message}
                            </p>
                            <div className={cn('mt-3 flex items-center gap-2 text-xs', isCapricho ? 'text-[#8B7355]/70' : 'text-white/40')} data-id="element-5934">
                              <span data-id="element-5935">📅 Pedidos: Lun–Vie</span>
                              <span data-id="element-5936">·</span>
                              <span data-id="element-5937">🚚 Entregas: Sáb y Dom 8am–4pm</span>
                            </div>
                          </div>

                          {/* Delivery Date Selection */}
                          <div data-id="element-5938">
                            <label className={cn('block text-sm font-semibold mb-3', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5939">
                              {isCapricho ? 'Fecha de entrega' : 'Fecha de entrega (sábado)'}
                            </label>
                            <div className="space-y-2" data-id="element-5940">
                              {deliveryDates.map(d => {
                      const dateStr = toDateString(d);
                      const isSelected = preorderDate === dateStr;
                      return <button key={dateStr} type="button" onClick={() => handlePreorderDateSelect(dateStr)} className={cn('w-full p-3.5 rounded-xl border-2 text-sm font-semibold transition-all text-left', isSelected ? isCapricho ? 'border-[#8B5E3C] bg-[#F5E9DA] text-[#3D2B1F]' : 'border-[#F4D35E] bg-[#F4D35E]/10 text-white' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#8B7355] hover:border-[#8B5E3C]/30' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-5941">
                                    📅 {formatDeliveryDate(d)}
                                  </button>;
                    })}
                            </div>
                          </div>

                          {/* Capricho: Delivery Hour Picker — HIDDEN, replaced by dynamic time slots below */}

                          {/* Capacity Check Status */}
                          {preorderDate && checkingCapacity && <div className={cn('flex items-center gap-2 p-3.5 rounded-xl border text-sm', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#8B7355]' : 'bg-[#141414] border-white/10 text-white/60')} data-id="element-5942">
                              <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-5943">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-5944" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-5945" />
                              </svg>
                              Verificando disponibilidad...
                            </div>}

                          {/* Capacity Full Message */}
                          {preorderDate && preorderCapacityFull && !checkingCapacity && <div className={cn('p-4 rounded-xl border text-sm', isCapricho ? 'bg-[#F5E9DA] border-[#E8DDD0]' : 'bg-[#141414] border-white/10')} data-id="element-5946">
                                <p className={cn('font-semibold mb-1', isCapricho ? 'text-[#8B5E3C]' : 'text-[#F4D35E]')} data-id="element-5947">
                                  ✨ {CAPACITY_FULL_MESSAGE}
                                </p>
                              </div>}

                          {/* Time Slot Selection — Dynamic from Supabase */}
                          {preorderDate && !preorderCapacityFull && !checkingCapacity && <div data-id="element-5948">
                                <label className={cn('block text-sm font-semibold mb-3', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5949">
                                  Horario de entrega
                                </label>
                                {timeSlotsLoading ? <div className={cn('flex items-center gap-2 p-3.5 rounded-xl border-2 text-sm', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#8B7355]' : 'bg-[#141414] border-white/10 text-white/60')} data-id="element-5950">
                                    <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-5951">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-5952" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-5953" />
                                    </svg>
                                    Cargando horarios...
                                  </div> : <div className="grid grid-cols-2 gap-2" data-id="element-5954">
                                    {dynamicTimeSlots.map(slot => {
                      const isSelected = preorderTimeSlot === slot.label;
                      const isFull = !slot.available;
                      const spotsLeft = slot.max_orders - slot.currentOrders;
                      return <button key={slot.id} type="button" disabled={isFull} onClick={() => setPreorderTimeSlot(slot.label)} className={cn('p-3 rounded-xl border-2 text-xs font-semibold transition-all relative', isFull ? isCapricho ? 'border-[#E8DDD0] bg-[#FBF7F2] text-[#8B7355]/40 cursor-not-allowed' : 'border-white/5 bg-[#141414]/50 text-white/20 cursor-not-allowed' : isSelected ? isCapricho ? 'border-[#8B5E3C] bg-[#F5E9DA] text-[#3D2B1F]' : 'border-[#F4D35E] bg-[#F4D35E]/10 text-white' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#8B7355] hover:border-[#8B5E3C]/30' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-5955">
                                          <div className="flex items-center justify-center gap-1.5" data-id="element-5956">
                                            <span data-id="element-5957">🕐</span>
                                            <span data-id="element-5958">
                                              {slot.label.includes('–') ? `entre ${slot.label.split(' – ')[0]} y ${slot.label.split(' – ')[1]}` : `entre ${slot.label}`}
                                            </span>
                                          </div>
                                          {isFull ? <span className={cn('block text-[10px] mt-1 font-medium', isCapricho ? 'text-[#8B7355]/50' : 'text-white/20')} data-id="element-5959">
                                              Agotado
                                            </span> : spotsLeft <= 1 ? <span className={cn('block text-[10px] mt-1 font-medium', isCapricho ? 'text-[#CE1126]' : 'text-[#CE1126]')} data-id="element-5960">
                                              ¡Último cupo!
                                            </span> : null}
                                        </button>;
                    })}
                                  </div>}
                              </div>}
                        </div>;
            })()}

                  <div data-id="element-5961">
                    <label className={cn('block text-sm font-semibold mb-3', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5962">
                      Tipo de entrega
                    </label>
                    <div className={cn('grid gap-3', restaurantSettings?.accept_pickup !== false && restaurantSettings?.accept_delivery !== false ? 'grid-cols-2' : 'grid-cols-1')} data-id="element-5963">
                      {restaurantSettings?.accept_pickup !== false && <button type="button" onClick={() => setDeliveryType('pickup')} className={cn('p-4 rounded-xl border-2 text-sm font-semibold transition-all flex flex-col items-center justify-center gap-2', deliveryType === 'pickup' ? isCapricho ? 'border-[#8B5E3C] bg-[#8B5E3C] text-white shadow-md shadow-[#8B5E3C]/20' : 'border-[#006847] bg-[#006847] text-white shadow-md shadow-[#006847]/20' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#8B7355] hover:border-[#8B5E3C]/30' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-5964">
                          <Store className="w-5 h-5" data-id="element-5965" />
                          {isPreorderRestaurant(restaurant?.id) ? 'Recoger sábado' : 'Para llevar'}
                        </button>}
                      {restaurantSettings?.accept_delivery !== false && <button type="button" onClick={() => setDeliveryType('delivery')} className={cn('p-4 rounded-xl border-2 text-sm font-semibold transition-all flex flex-col items-center justify-center gap-2', deliveryType === 'delivery' ? isCapricho ? 'border-[#8B5E3C] bg-[#8B5E3C] text-white shadow-md shadow-[#8B5E3C]/20' : 'border-[#006847] bg-[#006847] text-white shadow-md shadow-[#006847]/20' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#8B7355] hover:border-[#8B5E3C]/30' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-5966">
                          <Bike className="w-5 h-5" data-id="element-5967" />
                          {isPreorderRestaurant(restaurant?.id) ? 'Delivery sábado' : 'Delivery'}
                        </button>}
                    </div>
                  </div>

                  {deliveryType === 'delivery' && <div className={cn('space-y-4 p-4 rounded-xl border', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#141414] border-white/10')} data-id="element-5968">
                      <label className={cn('block text-sm font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5969">
                        Ubicación de entrega
                      </label>
                      <button type="button" onClick={handleGetLocation} disabled={isLocating} className={cn('w-full flex items-center justify-center gap-2 p-3.5 rounded-xl border-2 text-sm font-semibold transition-all', mapsUrl ? isCapricho ? 'border-[#8B5E3C]/30 bg-[#F5E9DA] text-[#8B5E3C]' : 'border-[#006847]/30 bg-[#006847]/10 text-[#2ECC71]' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#3D2B1F] hover:border-[#8B5E3C]/30 shadow-sm' : 'border-white/10 bg-[#141414] text-white hover:border-white/20 shadow-sm')} data-id="element-5970">
                        {isLocating ? <>
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-5971">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-5972" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-5973" />
                            </svg>
                            Obteniendo ubicación...
                          </> : mapsUrl ? <>
                            <CheckCircle2 className="w-5 h-5" data-id="element-5974" />
                            Ubicación capturada
                          </> : <>
                            <MapPin className="w-5 h-5" data-id="element-5975" />
                            Compartir mi ubicación
                          </>}
                      </button>
                      {mapsUrl && <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={cn('block text-xs underline underline-offset-2 truncate px-1', isCapricho ? 'text-[#C8A96A]' : 'text-[#F4D35E]')} data-id="element-5976">
                          {mapsUrl}
                        </a>}
                      {locationError && <p className={cn('text-sm border rounded-lg px-3 py-2.5', isCapricho ? 'text-[#CE1126] bg-[#CE1126]/5 border-[#CE1126]/20' : 'text-[#CE1126] bg-[#CE1126]/10 border-[#CE1126]/20')} data-id="element-5977">
                          {locationError}
                        </p>}
                      <div className="pt-2 space-y-1.5" data-id="element-5978">
                        <label className={cn('block text-sm font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5979">
                          Dirección exacta
                        </label>
                        <input type="text" placeholder="Ej: 200m norte del parque central, casa azul" value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} className={cn('w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:border-transparent', isCapricho ? 'bg-white border-[#E8DDD0] text-[#3D2B1F] placeholder-[#8B7355]/50 focus:ring-[#8B5E3C]' : 'bg-[#141414] border-white/10 text-white placeholder-white/30 focus:ring-[#006847]')} data-id="element-5980" />
                      </div>
                    </div>}

                  <div data-id="element-5981">
                    <label className={cn('block text-sm font-semibold mb-2', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5982">
                      Notas (opcional)
                    </label>
                    <textarea value={orderNotes} onChange={e => setOrderNotes(e.target.value)} placeholder="Instrucciones especiales..." rows={3} className={cn('w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:border-transparent resize-none', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#3D2B1F] placeholder-[#8B7355]/50 focus:ring-[#8B5E3C]' : 'bg-[#141414] border-white/10 text-white placeholder-white/30 focus:ring-[#006847]')} data-id="element-5983" />
                  </div>

                  {/* Order Summary */}
                  <div className={cn('rounded-xl p-5 space-y-3 text-sm border', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#141414] border-white/10')} data-id="element-5984">
                    <h4 className={cn('font-bold mb-3 text-base', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5985">
                      Resumen del pedido
                    </h4>
                    {cart.items.map(item => {
                const optTotal = item.selected_options.reduce((s, o) => s + o.price_delta, 0);
                const lineTotal = (item.unit_price_snapshot + optTotal) * item.quantity;
                return <div key={item.id} className={cn('flex justify-between', isCapricho ? 'text-[#8B7355]' : 'text-white/70')} data-id="element-5986">
                          <span className="pr-4" data-id="element-5987">
                            <span className={cn('font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5988">
                              {item.quantity}x
                            </span>{' '}
                            {item.item_name_snapshot}
                          </span>
                          <span className={cn('font-medium shrink-0', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5989">
                            {formatCurrency(lineTotal)}
                          </span>
                        </div>;
              })}
                    {deliveryType === 'delivery' && restaurant.express_fee > 0 && <div className={cn('flex justify-between pt-3 border-t mt-3', isCapricho ? 'text-[#8B7355] border-[#E8DDD0]' : 'text-white/70 border-white/10')} data-id="element-5990">
                          <span data-id="element-5991">Envío</span>
                          <span className={cn('font-medium', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-5992">
                            {formatCurrency(restaurant.express_fee)}
                          </span>
                        </div>}
                    {(() => {
                const disc = getDiscount();
                return disc ? <div className="flex justify-between pt-3 border-t mt-3 text-emerald-600 font-medium text-sm" style={{
                  borderColor: isCapricho ? '#E8DDD0' : 'rgba(255,255,255,0.1)'
                }} data-id="element-5993">
                          <span data-id="element-5994">{disc.label}</span>
                          <span data-id="element-5995">-{formatCurrency(disc.amount)}</span>
                        </div> : null;
              })()}
                    <div className={cn('flex justify-between font-bold pt-3 border-t text-base mt-3', isCapricho ? 'text-[#3D2B1F] border-[#E8DDD0]' : 'text-white border-white/10')} data-id="element-5996">
                      <span data-id="element-5997">Total</span>
                      <span className={isCapricho ? 'text-[#C8A96A]' : 'text-[#F4D35E]'} data-id="element-5998">
                        {formatCurrency(cartTotal + (deliveryType === 'delivery' ? restaurant.express_fee || 0 : 0))}
                      </span>
                    </div>
                  </div>

                  {/* Terms & Conditions */}
                  <div className={cn('rounded-xl p-4 border', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#141414] border-white/10')} data-id="element-5999">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-6000">
                      <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className={cn('mt-0.5 w-5 h-5 rounded shrink-0', isCapricho ? 'accent-[#8B5E3C]' : 'accent-[#006847]')} data-id="element-6001" />
                      <span className={cn('text-sm leading-relaxed', isCapricho ? 'text-[#8B7355]' : 'text-white/60')} data-id="element-6002">
                        Acepto los{' '}
                        <button type="button" onClick={() => setShowTermsModal(true)} className={cn('font-semibold underline underline-offset-2', isCapricho ? 'text-[#8B5E3C]' : 'text-[#F4D35E]')} data-id="element-6003">
                          Términos y Condiciones
                        </button>{' '}
                        y la{' '}
                        <button type="button" onClick={() => setShowTermsModal(true)} className={cn('font-semibold underline underline-offset-2', isCapricho ? 'text-[#8B5E3C]' : 'text-[#F4D35E]')} data-id="element-6004">
                          Política de Privacidad
                        </button>{' '}
                        de NOIZA.
                      </span>
                    </label>
                  </div>

                  {submitError && <div className={cn('border text-sm font-medium rounded-xl px-4 py-3 flex items-start gap-2', isCapricho ? 'bg-[#CE1126]/5 border-[#CE1126]/20 text-[#CE1126]' : 'bg-[#CE1126]/10 border-[#CE1126]/20 text-[#CE1126]')} data-id="element-6005">
                      <X className="w-5 h-5 shrink-0" data-id="element-6006" />
                      <p data-id="element-6007">{submitError}</p>
                    </div>}
                </div>

                <div className={cn('border-t p-6 shrink-0', isCapricho ? 'bg-white border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/10')} data-id="element-6008">
                  {isKitchenPaused && <div className="bg-red-900/40 border border-red-500/30 rounded-xl px-4 py-3 mb-3 text-center" data-id="element-6009">
                      <p className="text-red-300 text-sm font-medium" data-id="element-6010">
                        🔴 Cocina pausada — no se pueden enviar pedidos
                      </p>
                    </div>}
                  {isClosedBySchedule && <div className={cn('rounded-xl px-4 py-3 mb-3 text-center border', isCapricho ? 'bg-red-50 border-red-200' : 'bg-red-900/40 border-red-500/30')} data-id="element-6011">
                      <p className={cn('text-sm font-medium', isCapricho ? 'text-red-700' : 'text-red-300')} data-id="element-6012">
                        🔒 Restaurante cerrado —{' '}
                        {scheduleStatus.nextOpeningHint || 'no se pueden enviar pedidos'}
                      </p>
                    </div>}
                  <button onClick={handleSubmitOrder} disabled={isSubmitting || isKitchenPaused || isClosedBySchedule || !termsAccepted || !customerName.trim() || !customerPhone.trim() || preorderCapacityFull || checkingCapacity || isPreorderRestaurant(restaurant?.id) && (!preorderDate || !preorderTimeSlot)} className={cn('w-full h-14 rounded-xl text-[15px] font-bold flex items-center justify-center transition-all', isSubmitting || isKitchenPaused || isClosedBySchedule || !termsAccepted || !customerName.trim() || !customerPhone.trim() || preorderCapacityFull || checkingCapacity || isPreorderRestaurant(restaurant?.id) && (!preorderDate || !preorderTimeSlot) ? isCapricho ? 'bg-[#FBF7F2] text-[#8B7355]/50 border border-[#E8DDD0] cursor-not-allowed' : 'bg-[#141414] text-white/30 cursor-not-allowed' : isCapricho ? 'bg-[#8B5E3C] hover:bg-[#704A2E] text-white shadow-lg shadow-[#8B5E3C]/20' : 'bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-[#25D366]/30')} data-id="element-6013">
                    {isSubmitting ? <span className="flex items-center" data-id="element-6014">
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" data-id="element-6015">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-6016" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-6017" />
                        </svg>
                        Procesando...
                      </span> : <>
                        <MessageCircle className="w-5 h-5 mr-2" data-id="element-6018" />
                        {isPreorderRestaurant(restaurant?.id) ? 'Confirmar Preorden por WhatsApp' : 'Confirmar y enviar por WhatsApp'}
                      </>}
                  </button>
                </div>
              </>}

            {/* Step: Success — DISABLED (order confirmation now handled by OrderConfirmationPage) */}
            {false && cartStep === 'success' && completedOrder && <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center text-center" data-id="element-6019">
                <div className={cn('w-16 h-16 rounded-full flex items-center justify-center mb-4', completedOrder.whatsappSent ? isCapricho ? 'bg-[#F5E9DA]' : 'bg-[#2ECC71]/10' : isCapricho ? 'bg-[#FFF3CD]' : 'bg-amber-500/10')} data-id="element-6020">
                  {completedOrder.whatsappSent ? <CheckCircle2 className={cn('w-8 h-8', isCapricho ? 'text-[#8B5E3C]' : 'text-[#2ECC71]')} data-id="element-6021" /> : <MessageCircle className={cn('w-8 h-8', isCapricho ? 'text-[#8B5E3C]' : 'text-amber-400')} data-id="element-6022" />}
                </div>
                <h3 className={cn('text-2xl font-bold mb-1', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
            fontFamily: "'Playfair Display', serif"
          } : undefined} data-id="element-6023">
                  {completedOrder.whatsappSent ? '¡Pedido enviado!' : '¡Ya casi está listo!'}
                </h3>
                <p className={cn('mb-2 text-sm', isCapricho ? 'text-[#8B7355]' : 'text-white/60')} data-id="element-6024">
                  Orden{' '}
                  <strong className={isCapricho ? 'text-[#3D2B1F]' : 'text-white'} data-id="element-6025">
                    #{completedOrder.orderNumber}
                  </strong>
                </p>

                {!completedOrder.whatsappSent && <>
                    {/* Big WhatsApp button */}
                    <button onClick={() => {
              if (!completedOrder.whatsappUrl) {
                console.error('[NOIZA] No whatsappUrl available — cannot open WhatsApp');
                return;
              }
              // 1. Open WhatsApp SYNCHRONOUSLY to avoid popup blocker
              const popup = window.open('', '_blank');
              // 2. Update order status to 'new' (non-blocking, fire-and-forget)
              supabase.from('orders').update({
                status: 'new'
              }).eq('id', completedOrder.orderId).then(({
                error
              }) => {
                if (error) console.error('[NOIZA] Error updating order status:', error);else console.log('[NOIZA] Order status updated to new:', completedOrder.orderId);
              });
              // 3. Navigate the already-opened popup to WhatsApp
              if (popup) {
                popup.location.href = completedOrder.whatsappUrl;
              } else {
                // Fallback: redirect current tab if popup was blocked
                window.location.href = completedOrder.whatsappUrl;
              }
              // 4. Mark as sent
              setCompletedOrder(prev => prev ? {
                ...prev,
                whatsappSent: true
              } : prev);
            }} className={cn('w-full h-14 rounded-xl text-lg font-black flex items-center justify-center gap-2 transition-all mb-4 mt-4', isCapricho ? 'bg-[#25D366] hover:bg-[#20bd5a] shadow-lg shadow-[#25D366]/30 animate-pulse' : 'bg-[#25D366] hover:bg-[#20bd5a] shadow-lg shadow-[#25D366]/30 animate-pulse')} data-id="element-6026">
                      <MessageCircle className="w-6 h-6" data-id="element-6027" />
                      Enviar pedido por WhatsApp
                    </button>

                    {/* Red warning */}
                    <div className="w-full p-3 rounded-xl bg-red-600/10 border-2 border-red-500/30 mb-6" data-id="element-6028">
                      <p className="text-red-500 font-black text-sm sm:text-base leading-tight" data-id="element-6029">
                        ⚠️ SI NO ENVÍAS POR WHATSAPP, TU PEDIDO NO SE REALIZA
                      </p>
                    </div>
                  </>}

                {completedOrder.whatsappSent && <div className={cn('w-full p-4 rounded-2xl border mb-6 mt-4', isCapricho ? 'bg-[#F5E9DA] border-[#E8DDD0]' : 'bg-[#2ECC71]/10 border-[#2ECC71]/20')} data-id="element-6030">
                    <p className={cn('text-sm font-semibold', isCapricho ? 'text-[#8B5E3C]' : 'text-[#2ECC71]')} data-id="element-6031">
                      ✅ Tu pedido ha sido enviado al restaurante. ¡Gracias!
                    </p>
                  </div>}

                <button onClick={() => {
            setIsCartOpen(false);
            clearCart();
          }} className={cn('text-sm font-bold transition-colors', isCapricho ? 'text-[#8B7355] hover:text-[#3D2B1F]' : 'text-white/50 hover:text-white')} data-id="element-6032">
                  Cerrar y volver al menú
                </button>
              </div>}
          </div>
        </div>}

      {/* ═══ UPSELL MODAL ═══ */}
      {showUpsellModal && <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-id="element-6033">
          <div className="fixed inset-0 bg-black/80" onClick={() => {
        setShowUpsellModal(false);
        setIsCartOpen(false);
        const s = restaurant?.slug || cart.restaurantSlug || slug;
        navigate(s ? `/r/${s}/cart` : '/cart');
      }} data-id="element-6034" />
          <div className={cn('relative w-full sm:max-w-lg max-h-[85vh] sm:rounded-2xl rounded-t-2xl shadow-lg flex flex-col anim-slide-up border', isCapricho ? 'bg-white border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/10')} data-id="element-6035">
            <div className="px-6 pt-6 pb-4 shrink-0" data-id="element-6036">
              <div className="flex items-center justify-between mb-1" data-id="element-6037">
                <h3 className={cn('text-xl font-black', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
              fontFamily: "'Playfair Display', serif"
            } : undefined} data-id="element-6038">
                  ¿Le agregamos algo más? 🤤
                </h3>
                <button onClick={() => {
              setShowUpsellModal(false);
              setIsCartOpen(false);
              const s = restaurant?.slug || cart.restaurantSlug || slug;
              navigate(s ? `/r/${s}/cart` : '/cart');
            }} className={cn('p-1.5 transition-colors', isCapricho ? 'text-[#8B7355] hover:text-[#3D2B1F]' : 'text-white/40 hover:text-white')} data-id="element-6039">
                  <X className="w-5 h-5" data-id="element-6040" />
                </button>
              </div>
              <p className={cn('text-sm', isCapricho ? 'text-[#8B7355]' : 'text-white/50')} data-id="element-6041">
                Complementa tu pedido con estas opciones
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-4" data-id="element-6042">
              {upsellGroups.map(group => <div key={group.category.id} className="mb-6 last:mb-2" data-id="element-6043">
                  <h4 className={cn('text-sm font-bold mb-3 uppercase tracking-wider', isCapricho ? 'text-[#8B5E3C]' : 'text-[#F4D35E]')} data-id="element-6044">
                    {getCategoryEmoji(group.category.name)}{' '}
                    {group.category.name}
                  </h4>
                  <div className="space-y-2" data-id="element-6045">
                    {group.items.map(item => <div key={item.id} className={cn('flex items-center gap-3 rounded-xl p-3 border', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#141414] border-white/5')} data-id="element-6046">
                        {item.image_url ? <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-xl object-cover shrink-0" loading="lazy" decoding="async" data-id="element-6047" /> : <div className={cn('w-14 h-14 rounded-xl flex items-center justify-center shrink-0', isCapricho ? 'bg-[#F5E9DA]' : 'bg-[#1a1a1a]')} data-id="element-6048">
                            <span className="text-xl opacity-30" data-id="element-6049">
                              {getCategoryEmoji(group.category.name)}
                            </span>
                          </div>}
                        <div className="flex-1 min-w-0" data-id="element-6050">
                          <p className={cn('text-sm font-semibold truncate', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6051">
                            {item.name}
                          </p>
                          <p className={cn('text-sm font-bold', isCapricho ? 'text-[#C8A96A]' : 'text-[#F4D35E]')} data-id="element-6052">
                            {formatCurrency(item.price)}
                          </p>
                        </div>
                        <button onClick={() => handleQuickAdd(item)} className={cn('w-9 h-9 rounded-full flex items-center justify-center transition-colors shadow-md shrink-0 active:scale-90', isCapricho ? 'bg-[#8B5E3C] text-white hover:bg-[#704A2E] shadow-[#8B5E3C]/20' : 'bg-[#006847] text-white hover:bg-[#005a3d] shadow-[#006847]/20')} data-id="element-6053">
                          <Plus className="w-4 h-4" data-id="element-6054" />
                        </button>
                      </div>)}
                  </div>
                </div>)}
              {upsellGroups.length === 0 && <p className={cn('text-center py-8 text-sm', isCapricho ? 'text-[#8B7355]' : 'text-white/40')} data-id="element-6055">
                  No hay sugerencias disponibles
                </p>}
            </div>

            <div className={cn('px-6 pb-6 pt-3 shrink-0 border-t space-y-2.5', isCapricho ? 'border-[#E8DDD0]' : 'border-white/5')} data-id="element-6056">
              <button onClick={() => setShowUpsellModal(false)} className={cn('w-full h-12 rounded-xl border font-bold text-sm transition-colors', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#8B5E3C] hover:bg-[#F5E9DA]' : 'bg-[#141414] border-white/10 text-white hover:bg-[#1a1a1a]')} data-id="element-6057">
                Seguir agregando
              </button>
              <button onClick={() => {
            setShowUpsellModal(false);
            setIsCartOpen(false);
            const s = restaurant?.slug || cart.restaurantSlug || slug;
            navigate(s ? `/r/${s}/cart` : '/cart');
          }} className={cn('w-full h-12 rounded-xl font-bold text-sm shadow-lg transition-colors', isCapricho ? 'bg-[#8B5E3C] text-white shadow-[#8B5E3C]/30 hover:bg-[#704A2E]' : 'bg-[#006847] text-white shadow-[#006847]/30 hover:bg-[#005a3d]')} data-id="element-6058">
                Continuar al pedido →
              </button>
            </div>
          </div>
        </div>}

      {/* ═══ PRODUCT DETAIL MODAL ═══ */}
      <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={restaurant.primary_color || undefined} theme={isCapricho ? 'capricho' : 'default'} data-id="element-6059" />

      {/* ═══ CAPRICHO WELCOME MODAL ═══ */}
      {showCaprichoWelcome && isCapricho && <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-id="element-6060">
          <div className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" style={{
        backgroundColor: '#FBF7F2'
      }} data-id="element-6061">
            {/* Header accent */}
            <div className="h-2 w-full" style={{
          background: 'linear-gradient(90deg, #8B5E3C, #C8A96A, #8B5E3C)'
        }} data-id="element-6062" />

            <div className="p-6 space-y-5" data-id="element-6063">
              {/* Logo + Title */}
              <div className="text-center" data-id="element-6064">
                <div className="w-16 h-16 mx-auto mb-3 rounded-xl overflow-hidden border-2 border-[#E8DDD0] bg-white" data-id="element-6065">
                  <img src="https://cdn.magicpatterns.com/uploads/qfQUG6xSwCibNddEpouoD3/ChatGPT_Image_21_mar_2026,_21_58_30.png" alt="Capricho de Canela" className="w-full h-full object-contain p-1" loading="lazy" decoding="async" data-id="element-6066" />
                </div>
                <h2 className="text-xl font-bold text-[#3D2B1F]" style={{
              fontFamily: "'Playfair Display', serif"
            }} data-id="element-6067">
                  ¡Bienvenido a Capricho de Canela!
                </h2>
              </div>

              {/* Info items */}
              <div className="space-y-3" data-id="element-6068">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-6069">
                  <span className="text-lg shrink-0" data-id="element-6070">📦</span>
                  <p className="text-sm text-[#3D2B1F] font-medium" data-id="element-6071">
                    {(() => {
                  const now = new Date();
                  const isSpecialSaturday = now.getFullYear() === 2026 && now.getMonth() === 4 && now.getDate() === 2;
                  return isSpecialSaturday ? <>
                          ¡Hoy sábado estamos recibiendo pedidos! Entrega{' '}
                          <strong data-id="element-6072">mañana domingo</strong>.
                        </> : <>
                          Recibimos tus pedidos de{' '}
                          <strong data-id="element-6073">Lunes a Viernes</strong>.
                        </>;
                })()}
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-6074">
                  <span className="text-lg shrink-0" data-id="element-6075">🚚</span>
                  <p className="text-sm text-[#3D2B1F] font-medium" data-id="element-6076">
                    Entregamos <strong data-id="element-6077">Sábado y Domingo</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-6078">
                  <span className="text-lg shrink-0" data-id="element-6079">📅</span>
                  <p className="text-sm text-[#3D2B1F] font-medium" data-id="element-6080">
                    Próxima fecha de entrega:{' '}
                    <strong data-id="element-6081">
                      {(() => {
                    const dates = getUpcomingDeliveryDates(1);
                    if (dates.length === 0) return 'Próximamente';
                    const d = dates[0];
                    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
                    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                    return `${dayNames[d.getDay()]} ${d.getDate()} de ${monthNames[d.getMonth()]}`;
                  })()}
                    </strong>
                    .
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-6082">
                  <span className="text-lg shrink-0" data-id="element-6083">😍</span>
                  <p className="text-sm text-[#3D2B1F] font-medium" data-id="element-6084">
                    ¡Estás listo para elegir tu capricho!
                  </p>
                </div>
              </div>

              {/* CTA Button */}
              <button onClick={() => {
            setShowCaprichoWelcome(false);
            sessionStorage.setItem('capricho-welcome-dismissed', 'true');
          }} className="w-full py-3.5 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90 active:scale-[0.98]" style={{
            backgroundColor: '#8B5E3C'
          }} data-id="element-6085">
                🍪 ¡Quiero mi Capricho!
              </button>
            </div>
          </div>
        </div>}
      {/* ═══ TERMS & CONDITIONS MODAL ═══ */}
      {showTermsModal && <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowTermsModal(false)} data-id="element-6086">
          <div className={cn('relative w-full max-w-lg max-h-[80vh] rounded-2xl shadow-2xl flex flex-col', isCapricho ? 'bg-white' : 'bg-[#0B0B0B] border border-white/10')} onClick={e => e.stopPropagation()} data-id="element-6087">
            <div className={cn('flex items-center justify-between px-6 py-4 border-b shrink-0', isCapricho ? 'border-[#E8DDD0]' : 'border-white/10')} data-id="element-6088">
              <h2 className={cn('text-lg font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6089">
                Términos y Condiciones
              </h2>
              <button onClick={() => setShowTermsModal(false)} className={cn('p-2 -mr-2 rounded-full transition-colors', isCapricho ? 'text-[#8B7355] hover:bg-[#F5E9DA]' : 'text-white/40 hover:bg-white/10')} data-id="element-6090">
                <X className="w-5 h-5" data-id="element-6091" />
              </button>
            </div>
            <div className={cn('flex-1 overflow-y-auto px-6 py-5 text-sm leading-relaxed space-y-4', isCapricho ? 'text-[#8B7355]' : 'text-white/60')} data-id="element-6092">
              <h3 className={cn('font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6093">
                1. Aceptación de los Términos
              </h3>
              <p data-id="element-6094">
                Al realizar un pedido a través de NOIZA, usted acepta estos
                términos y condiciones en su totalidad. Si no está de acuerdo
                con alguna parte, no debe utilizar nuestro servicio.
              </p>
              <h3 className={cn('font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6095">
                2. Servicio de Pedidos
              </h3>
              <p data-id="element-6096">
                NOIZA actúa como intermediario tecnológico entre el cliente y el
                restaurante. Los pedidos se procesan y confirman directamente
                con el restaurante a través de WhatsApp.
              </p>
              <h3 className={cn('font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6097">
                3. Precios y Pagos
              </h3>
              <p data-id="element-6098">
                Los precios mostrados incluyen impuestos. El pago se realiza
                directamente al restaurante según el método seleccionado
                (efectivo, SINPE Móvil o tarjeta). NOIZA no procesa pagos.
              </p>
              <h3 className={cn('font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6099">
                4. Entregas
              </h3>
              <p data-id="element-6100">
                Los tiempos de entrega son estimados y pueden variar según la
                demanda. El costo de envío se muestra antes de confirmar el
                pedido. El restaurante es responsable de la entrega.
              </p>
              <h3 className={cn('font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6101">
                5. Cancelaciones
              </h3>
              <p data-id="element-6102">
                Las cancelaciones deben coordinarse directamente con el
                restaurante a través de WhatsApp. Una vez que el pedido está en
                preparación, no se garantiza la cancelación.
              </p>
              <h3 className={cn('font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6103">
                6. Datos Personales
              </h3>
              <p data-id="element-6104">
                Recopilamos nombre, teléfono y ubicación únicamente para
                procesar su pedido. No compartimos sus datos con terceros más
                allá del restaurante seleccionado. Su información se maneja
                conforme a la Ley de Protección de Datos de Costa Rica.
              </p>
              <h3 className={cn('font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6105">
                7. Responsabilidad
              </h3>
              <p data-id="element-6106">
                NOIZA no es responsable por la calidad de los alimentos, tiempos
                de entrega o cualquier inconveniente con el restaurante.
                Cualquier reclamo debe dirigirse directamente al
                establecimiento.
              </p>
              <h3 className={cn('font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-6107">
                8. Modificaciones
              </h3>
              <p data-id="element-6108">
                Nos reservamos el derecho de modificar estos términos en
                cualquier momento. Los cambios serán efectivos al publicarse en
                la plataforma.
              </p>
              <p className={cn('text-xs pt-4 border-t', isCapricho ? 'text-[#8B7355]/50 border-[#E8DDD0]' : 'text-white/20 border-white/5')} data-id="element-6109">
                Última actualización: Marzo 2026 · Los Santos, Costa Rica
              </p>
            </div>
            <div className={cn('px-6 py-4 border-t shrink-0', isCapricho ? 'border-[#E8DDD0]' : 'border-white/10')} data-id="element-6110">
              <button onClick={() => {
            setTermsAccepted(true);
            setShowTermsModal(false);
          }} className={cn('w-full py-3 rounded-xl font-bold text-sm transition-colors', isCapricho ? 'bg-[#8B5E3C] text-white hover:bg-[#704A2E]' : 'bg-[#006847] text-white hover:bg-[#005a3d]')} data-id="element-6111">
                Aceptar Términos y Condiciones
              </button>
            </div>
          </div>
        </div>}
      {showOpenOrderAlert && openOrder && <OpenOrderAlert order={openOrder} onDismiss={handleDismissOpenOrder} onViewTracking={handleCancelOpenOrder} data-id="element-6112" />}
    </div>;
}